import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import {
  parentOf,
  shortUuid,
  type Container,
  type DeadlineFilter,
} from "@egalregal/shared";
import { log } from "../../logger";
import { ChevronDownIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { usePreferencesStore } from "../../shared/store/preferences";
import { subtreeKinds, type ContainerKindFilter } from "../../shared/lib/containerMedia";
import { linkUnderParent, linkTargetExclusions } from "./link-child";
import { PickerDialog } from "./PickerDialog";
import { MoveEntryDialog } from "../catalogs/MoveEntryDialog";
import { ScannerSurface } from "../scan/ScannerSurface";
import { useDemoMode } from "../demo/useDemoMode";

/** The container view's single action line (container-view 01, round 5). */
interface ContainerMenuProps {
  /** The container being viewed; null at the storage root. */
  current?: Container;
  deadlineView: boolean;
  onToggleDeadlineView: (enabled: boolean) => void;
  deadlineFilters: readonly DeadlineFilter[];
  onDeadlineFilters: (next: DeadlineFilter[]) => void;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  /** Reveal the details block (enabling sub-container media needs it visible). */
  onOpenDetails: () => void;
  bulkMode: boolean;
  /** Bulk selection belongs to one catalog entry; no entry, no Select. */
  canSelect: boolean;
  onToggleBulkMode: () => void;
}

/**
 * The containers row's right-aligned controls: one labelled `More ▾` menu and
 * the contained `Edit` button. The menu holds every container action in three
 * groups — View (details fold, deadline column and filters, sub-container media
 * and its container-kind filter), Container (add sub-container, bulk select)
 * and Tree (link, move, scan to link, unlink).
 *
 * Tree operations keep their existing dialogs. Link attaches the current
 * container under the picked parent (container-view 05); Add sub-container
 * opens the create form scoped to the current container.
 */
export function ContainerMenu({
  current,
  deadlineView,
  onToggleDeadlineView,
  deadlineFilters,
  onDeadlineFilters,
  detailsOpen,
  onToggleDetails,
  onOpenDetails,
  bulkMode,
  canSelect,
  onToggleBulkMode,
}: ContainerMenuProps) {
  const t = useT();
  const demoMode = useDemoMode();
  const navigate = useNavigate();
  // Tree actions stay on the entry-scoped slice; the media kind list comes from
  // the readable union, like the media collection itself.
  const containers = useAppStore((s) => s.containers);
  const readable = useAppStore(readableContainers);
  const catalogContainers = useAppStore((s) => s.catalogContainers);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const linkChild = useAppStore((s) => s.linkChild);
  const unlink = useAppStore((s) => s.unlink);
  const subtreeMedia = usePreferencesStore((s) => s.containerSubtreeMedia);
  const setSubtreeMedia = usePreferencesStore((s) => s.setContainerSubtreeMedia);
  const kindFilter: ContainerKindFilter = usePreferencesStore((s) => s.containerKindFilter);
  const setKindFilter = usePreferencesStore((s) => s.setContainerKindFilter);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [notice, setNotice] = useState<MenuNotice | null>(null);

  const close = () => setAnchor(null);
  const sourceOwner = sharedWithMe.find((entry) => entry.entry.id === selectedEntryId)?.owner;
  const hasParent = current ? Boolean(parentOf(containers, current.id)) : false;
  const excluded = current ? linkTargetExclusions(containers, current.id) : new Set<string>();
  // The kinds offered are the ones actually present below this container,
  // custom kinds included; "all" is the stored `null` sentinel.
  const kinds = current ? subtreeKinds(readable, current.id) : [];
  const selectedKinds = kindFilter === null ? new Set(kinds) : new Set(kindFilter);
  const active =
    deadlineView ||
    subtreeMedia ||
    deadlineFilters.length > 0 ||
    bulkMode ||
    (current !== undefined && !detailsOpen);

  /** Menu items close the menu first; check items keep it open for multi-select. */
  const item = (key: string, label: string, onSelect: () => void, testId?: string) => (
    <MenuItem
      key={key}
      data-testid={testId}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      {label}
    </MenuItem>
  );

  const check = (
    key: string,
    label: string,
    checked: boolean,
    onToggle: () => void,
    disabled = false,
  ) => (
    <MenuItem key={key} disabled={disabled} onClick={onToggle}>
      <Checkbox
        edge="start"
        size="small"
        checked={checked}
        tabIndex={-1}
        disableRipple
        inputProps={{ "aria-label": label }}
        sx={{ ml: -1, mr: 0.5 }}
      />
      <ListItemText primary={label} />
    </MenuItem>
  );

  const heading = (key: string, label: string) => (
    <ListSubheader key={key} disableSticky sx={{ lineHeight: "32px" }}>
      {label}
    </ListSubheader>
  );

  const toggleFilter = (filter: DeadlineFilter) =>
    onDeadlineFilters(
      deadlineFilters.includes(filter)
        ? deadlineFilters.filter((entry) => entry !== filter)
        : [...deadlineFilters, filter],
    );

  /** Enabling the media option needs the details block on screen. */
  const toggleSubtreeMedia = () => {
    const enabled = !subtreeMedia;
    setSubtreeMedia(enabled);
    if (enabled && !detailsOpen) onOpenDetails();
  };

  /**
   * Narrow or widen the kind filter. Re-checking the last missing kind returns
   * to the `null` "all" sentinel, so a kind added later participates again.
   */
  const toggleKind = (kind: string) => {
    const next = new Set(selectedKinds);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    const kept = kinds.filter((candidate) => next.has(candidate));
    setKindFilter(kept.length === kinds.length ? null : kept);
  };

  const linkTo = async (parentId: string) => {
    if (!current) return;
    try {
      await linkUnderParent(parentId, current.id);
      const parent = useAppStore.getState().containers.find((c) => c.id === parentId);
      setNotice({
        message: t("linkedAsChild", {
          child: current.name,
          parent: parent?.name ?? shortUuid(parentId),
        }),
        severity: "success",
      });
      // The link moved this container under a new parent; keep its own level
      // open so the refreshed breadcrumb shows the move.
      navigate(`/browse/${current.id}`);
    } catch (err) {
      log.warn({ err, containerId: current.id, parentId }, "Linking the container under the picked parent failed");
      setNotice({ message: t("linkFailed"), severity: "error" });
    }
  };

  const handleUnlink = async () => {
    if (!current) return;
    try {
      await unlink(current.id, current.version);
    } catch (err) {
      log.warn({ err, containerId: current.id }, "Unlinking the container failed");
      setNotice({ message: t("unlinkFailed"), severity: "error" });
    }
  };

  const scanToLink = async (uuid: string) => {
    if (!current) return;
    try {
      await linkChild(uuid, current.id);
      const scanned = useAppStore.getState().containers.find((c) => c.id === uuid);
      setNotice({
        message: t("linkedAsChild", {
          child: current.name,
          parent: scanned?.name ?? shortUuid(uuid),
        }),
        severity: "success",
      });
    } catch (err) {
      log.warn({ err, containerId: current.id, scannedId: uuid }, "Scan-to-link failed");
      throw err;
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          color={active ? "primary" : "inherit"}
          endIcon={<ChevronDownIcon fontSize="small" />}
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(event) => setAnchor(event.currentTarget)}
          data-testid="browse-more"
          sx={{ minHeight: 40, fontWeight: 600 }}
        >
          {t("more")}
        </Button>
        <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
          {heading("view", t("view"))}
          {current &&
            item(
              "details",
              detailsOpen ? t("hideDetails") : t("showDetails"),
              onToggleDetails,
            )}
          {check("column", t("deadlines"), deadlineView, () =>
            onToggleDeadlineView(!deadlineView),
          )}
          {check(
            "overdue",
            t("deadlineOverdue"),
            deadlineFilters.includes("overdue"),
            () => toggleFilter("overdue"),
            !deadlineView,
          )}
          {check(
            "soon",
            t("deadlineSoon"),
            deadlineFilters.includes("soon"),
            () => toggleFilter("soon"),
            !deadlineView,
          )}
          {current &&
            check(
              "subtree-media",
              t("includeSubContainerMedia"),
              subtreeMedia,
              toggleSubtreeMedia,
            )}
          {current &&
            subtreeMedia &&
            kinds.length > 0 &&
            heading("media-kinds", t("containerKinds"))}
          {current &&
            subtreeMedia &&
            kinds.map((kind) =>
              check(`media-kind-${kind}`, kind, selectedKinds.has(kind), () =>
                toggleKind(kind),
              ),
            )}
          {!demoMode && <Divider key="view-divider" />}
          {!demoMode && heading("container", t("menuContainer"))}
          {!demoMode &&
            item(
              "add-container",
              current ? t("addSubContainer") : t("addTitle"),
              () => navigate(current ? `/add?parent=${current.id}` : "/add"),
            )}
          {!demoMode &&
            canSelect &&
            item(
              "select",
              bulkMode ? t("cancelSelectionMode") : t("selectContainers"),
              onToggleBulkMode,
              "browse-bulk-toggle",
            )}
          {!demoMode && current && (
            <>
              <Divider key="container-divider" />
              {heading("tree", t("tree"))}
              {item("link", t("link"), () => setPickerOpen(true))}
              {item("move", t("move"), () => setMoveOpen(true))}
              {item("scan", t("scanToLink"), () => setScanOpen(true))}
              {hasParent && item("unlink", t("unlink"), () => void handleUnlink())}
            </>
          )}
        </Menu>
        {current && !demoMode && (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/edit/${current.id}`)}
            data-testid="browse-edit"
            sx={{ minHeight: 40 }}
          >
            {t("edit")}
          </Button>
        )}
      </Stack>

      {current && !demoMode && (
        <>
          <PickerDialog
            open={pickerOpen}
            title={t("linkChildTitle")}
            containers={containers}
            excludeIds={excluded}
            onPick={(parentId) => void linkTo(parentId)}
            onClose={() => setPickerOpen(false)}
          />

          <MoveEntryDialog
            open={moveOpen}
            container={current}
            sourceOwner={sourceOwner}
            onClose={() => setMoveOpen(false)}
            onMoved={(entryName, droppedPlacements) => {
              setNotice({
                message:
                  droppedPlacements > 0
                    ? t("movedDroppedPlacements", { count: String(droppedPlacements) })
                    : t("movedToEntry", { entry: entryName }),
                severity: "success",
              });
            }}
          />

          <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="xs">
            <ScannerSurface
              variant="identify"
              onResolved={scanToLink}
              onClose={() => setScanOpen(false)}
              hint={t("scanLinkHint")}
              resolveLabel={(code) =>
                containers.find((c) => c.id === code)?.name ??
                catalogContainers.find((c) => c.id === code)?.name
              }
              errorMessage={t("scanLinkFailed")}
            />
          </Dialog>
        </>
      )}

      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={notice?.severity ?? "success"} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </>
  );
}

interface MenuNotice {
  message: string;
  severity: "success" | "error";
}
